/**
 * FairnessCheck banner helpers (Rule 4b).
 * equivalent_readiness = ((proposed_amount - Min) / (Max - Min)) * 150
 */

export const FAIRNESS_STATE = {
  ABOVE: "above",
  BELOW: "below",
  EXACT: "exact",
  NEAR_EXACT: "near-exact",
  BELOW_MIN: "below-min",
  ABOVE_MAX: "above-max",
};

const TOLERANCE_PCT = 2;

export function computeEquivalentReadiness(proposedAmount, rangeMin, rangeMax) {
  const proposed = Number(proposedAmount);
  const min = Number(rangeMin);
  const max = Number(rangeMax);
  if (![proposed, min, max].every(Number.isFinite) || max === min) return null;
  return ((proposed - min) / (max - min)) * 150;
}

export function resolveFairnessState({
  proposedAmount,
  fairPayAmount,
  rangeMin,
  rangeMax,
  currentReadinessPct,
  tolerancePct = TOLERANCE_PCT,
}) {
  const proposed = Number(proposedAmount);
  const fair = Number(fairPayAmount);
  const min = Number(rangeMin);
  const max = Number(rangeMax);

  if (![proposed, min, max].every(Number.isFinite)) return null;
  if (proposed < min) return FAIRNESS_STATE.BELOW_MIN;
  if (proposed > max) return FAIRNESS_STATE.ABOVE_MAX;
  if (!Number.isFinite(fair)) return null;

  const delta = proposed - fair;
  if (delta === 0) return FAIRNESS_STATE.EXACT;

  const toleranceAbs = Math.abs(fair) * (Number(tolerancePct) / 100);
  if (Math.abs(delta) <= toleranceAbs) return FAIRNESS_STATE.NEAR_EXACT;
  if (delta > 0) return FAIRNESS_STATE.ABOVE;
  return FAIRNESS_STATE.BELOW;
}

export function formatFairnessMessage({
  state,
  proposedAmount,
  fairPayAmount,
  rangeMin,
  rangeMax,
  equivalentReadiness,
  currentReadinessPct,
  currencySymbol = "$",
}) {
  if (!state) return "";

  const money = (n) =>
    `${currencySymbol}${Math.abs(Number(n)).toLocaleString("en-US", {
      maximumFractionDigits: 0,
    })}`;
  const readiness =
    equivalentReadiness == null
      ? ""
      : `${Number(equivalentReadiness).toLocaleString("en-US", {
          maximumFractionDigits: 0,
        })}%`;

  switch (state) {
    case FAIRNESS_STATE.BELOW_MIN:
      return `${money(Number(rangeMin) - Number(proposedAmount))} below the pay range minimum of ${money(
        rangeMin
      )}`;
    case FAIRNESS_STATE.ABOVE_MAX:
      return `${money(Number(proposedAmount) - Number(rangeMax))} above the pay range maximum of ${money(
        rangeMax
      )}`;
    case FAIRNESS_STATE.EXACT:
      return `Fairly paid exactly — matches current readiness of ${currentReadinessPct}%`;
    case FAIRNESS_STATE.NEAR_EXACT: {
      const delta = Number(proposedAmount) - Number(fairPayAmount);
      const pct =
        Number(fairPayAmount) === 0
          ? 0
          : (Math.abs(delta) / Math.abs(Number(fairPayAmount))) * 100;
      const direction = delta < 0 ? "below" : "above";
      return `${money(delta)} ${direction} Fair Pay (${pct.toFixed(
        1
      )}% inside tolerance) with equivalent readiness of ${readiness}`;
    }
    case FAIRNESS_STATE.ABOVE:
      return `${money(Number(proposedAmount) - Number(fairPayAmount))} above Fair Pay with equivalent readiness of ${readiness}`;
    case FAIRNESS_STATE.BELOW:
      return `${money(Number(fairPayAmount) - Number(proposedAmount))} below Fair Pay with equivalent readiness of ${readiness}`;
    default:
      return "";
  }
}

export function fairnessBannerClass(state) {
  switch (state) {
    case FAIRNESS_STATE.EXACT:
    case FAIRNESS_STATE.NEAR_EXACT:
      return "fppw-fairness--exact";
    case FAIRNESS_STATE.ABOVE:
      return "fppw-fairness--above";
    case FAIRNESS_STATE.BELOW:
      return "fppw-fairness--below";
    case FAIRNESS_STATE.BELOW_MIN:
    case FAIRNESS_STATE.ABOVE_MAX:
      return "fppw-fairness--out-of-range";
    default:
      return "";
  }
}

export function fairnessBannerIcon(state) {
  switch (state) {
    case FAIRNESS_STATE.EXACT:
    case FAIRNESS_STATE.NEAR_EXACT:
      return "check-circle";
    case FAIRNESS_STATE.ABOVE:
    case FAIRNESS_STATE.BELOW:
    case FAIRNESS_STATE.BELOW_MIN:
    case FAIRNESS_STATE.ABOVE_MAX:
      return "alert-triangle";
    default:
      return null;
  }
}
